import { PeekyElement } from "@front/peeky/peeky.element";
import { PeekyState } from "@front/peeky/peeky.state";

type ActionFunction = ((...args: any) => Promise<void>);
type EventFunction = ((...args: any) => void);

export class PeekyEmitter {
  _action: { [key: string]: ActionFunction[] } = {};
  _event: { [key: string]: EventFunction[] } = {};

  // Add action-extension
  bindAction(name: string, func: ActionFunction): this;
  bindAction(name: string, func: ActionFunction) {
    if (!name || !func) {
      return this;
    }
    if (!this._action[name]) {
      this._action[name] = [];
    }
    this._action[name].push(func);
    return this;
  }

  //
  // Bind an action that is executed only once
  oneAction(name: string, func: ActionFunction): this;
  oneAction(name?: string, func?: ActionFunction): this {
    if (!name || !func) {
      return this;
    }
    let wrapper = (...args: any[]) => {
      this.unbindAction(name, wrapper);
      return func.apply(this, args);
    };
    (<any>wrapper)._one = true;
    this.bindAction(name, wrapper);
    return this;
  }


  //
  unbindAction(name: string, func: ActionFunction) {
    if (!name || !this._action[name]) {
      return this;
    }
    if (!func) {
      delete (this._action[name]);
      return this;
    }

    let actions = this._action[name];
    let index = actions.indexOf(func);
    while (index >= 0) {
      this._action[name].splice(index, 1);
      index = actions.indexOf(func);
    }
    return this;
  }


  async triggerAction(pseudo: boolean, name: string, parameters?: any): Promise<any>;
  async triggerAction(name: string, parameters?: any): Promise<any>;
  async triggerAction(...args: any[]): Promise<any> {
    let i = 0;
    let pseudo: boolean = typeof (args[i]) === 'boolean' ? args[i++] : false;
    let name: string = typeof (args[i]) === 'string' ? args[i++] : '';
    let parameters = typeof (args[i]) === 'object' ? args[i++] : {};

    if (!name) {
      return parameters;
    }

    // If this is not a "pseudo"-call, we should execute pre* and post* actions
    // before and after the actual action
    if (!pseudo) {
      let actionName = name.charAt(0).toUpperCase() + name.slice(1);
      // Pre action
      try {
        parameters = await this.triggerAction(true, 'pre' + actionName, parameters);
      } catch (e) {
        if (e instanceof Error) {
          throw e;
        }
        return parameters;
      }

      // Normal action
      try {
        parameters = await this.triggerAction(true, name, parameters);
      } catch (e) {
        if (e instanceof Error) {
          throw e;
        }
        return parameters;
      }

      // Post action
      try {
        parameters = await this.triggerAction(true, 'post' + actionName, parameters);
      } catch (e) {
        if (e instanceof Error) {
          throw e;
        }
        return parameters;
      }

      return parameters;
    }

    // Trigger events with same name
    this.trigger(name, parameters);

    // A "stateEmitter" is used to emit events through an action's lifetime, e.g. cancelling a goTo action etc.
    if (parameters._stateEmitter instanceof PeekyEmitter && this !== parameters._stateEmitter) {
      await parameters._stateEmitter.triggerAction(pseudo, name, parameters);
    }

    // Merge with possible incoming actionlist in parameters
		let paramActions = parameters.actions ? parameters.actions[name] : undefined;
    let actionList = [ ...this._action[name] || [], ...paramActions || [] ];

    // Execute list of actions
    for (let i=0; i < actionList.length; i++) {
      let action = actionList[i];
      if (!action) {
        continue;
      }
      let timeout: number | void = window.setTimeout(() => {
        console.log('Action ' + name + ':' + (<any>action)._plugin + ' has not called back in 5 seconds');
      }, 5000);

      try {
        parameters = await action.call(this, parameters) || parameters;
        if (timeout) {
          timeout = window.clearTimeout(timeout);
        }
      } catch (e) {
        if (e instanceof Error) {
          console.log('Error in action ' + name + ':' + (<any>action)._plugin + ':');
          throw e;
        }
        if (timeout) {
          timeout = window.clearTimeout(timeout);
        }
        // Remove all actions that should happen once
        while (i < actionList.length) {
          if (actionList[i] && (<any>actionList[i])._one) { // INSTANCEOF ONEWRAPPER?
            actionList.splice(i, 1);
          } else {
            i++;
          }
        }
        if (pseudo) {
          throw e;
        } else {
          if (typeof(e) !== 'undefined') {
            return e;
          } else {
            return parameters;
          }
        }
      }
    }

    return parameters;
  }


  //
  on(name: 'prepareSwitch', func: (this: this, param: any) => any): this;
  on(name: 'loadedState' | 'preLoadedState' | 'postLoadedState', func: (this: this, param: any, state?: PeekyState) => any): this;
  on(name: 'loadedElement' | 'preLoadedElement' | 'postLoadedElement', func: (this: this, param: any, element?: PeekyElement) => any): this;
  on(name: string, func: (this: this, ...args: any) => any): this;
  on(name?: string, func?): this {
    if (!name || !func) {
      return this;
    }
    let names = name.split(' ');
    for (let i = 0; i < names.length; i++) {
      if (!this._event[names[i]]) {
        this._event[names[i]] = [];
      }
      this._event[names[i]].push(func);
    }
    return this;
  }


  // Bind an action that is executed only once
  one(name: string, func: (this: this, ...args: any) => any): this;
  one(name?: string, func?) {
    if (!name || !func) {
      return this;
    }
    let wrapper = (...args: any[]) => {
      this.off(name, wrapper);
      func.apply(this, args);
    };
    this.on(name, wrapper);
    return this;
  }
  once(name: string, func) {
    return this.one.apply(this, arguments);
  }


  //
  // "target" is the object that triggered the event
  // If "target" isn't given, then "this" is the element that triggered the event.
  trigger(name: string, param?, target?, ...arg: any[]) {
    if (this._event[name]) {
      let events = this._event[name].slice();
      for (let i = 0; i < events.length; i++) {
        events[i].apply(this, Array.prototype.slice.call(arguments, 1));
      }
    }

    let parent = (<any>this).parent;
    let site = (<any>this).site;
    if (parent instanceof PeekyEmitter) {
      parent.trigger(name, param, target || this, ...arg);
    }
    if (!target && site instanceof PeekyEmitter) {
      site.trigger(name, param, this, ...arg);
    }

    return this;
  }
  emit(name: string, param?, target?) {
    return this.trigger(name, param, target);
  }


  //
  off(name: string, func) {
    if (!name || !this._event[name]) {
      return this;
    }
    if (!func) {
      delete (this._event[name]);
    } else {
      let events = this._event[name]
      let index = events.indexOf(func);
      while (index >= 0) {
        if (index === events.length - 1) {
          events.pop();
        } else if (index === 0) {
          events.shift();
        } else {
          events.splice(index, 1);
        }
        index = events.indexOf(func);
      }
    }
    return this;
  }

}

